import { isNotificationEvent, notificationCatalog, type NotificationEvent } from './catalog.js';

/**
 * Settings › Messages' Send and Copy switches (APP-194). Send is the organization's email switch,
 * `notifications.events` (System administration › Notifications shows the same one). Copy says
 * whether the copy list (APP-189) is copied on an email, `notifications.copyEmails`: a record of
 * the switches changed from their defaults, each read when the email is written. Pure.
 */

/**
 * Emails that ask someone to act, each with what stops when it's turned off, in the words of the
 * confirm: "People won't be emailed when …; it still shows on their Home."
 */
export const ACTION_EMAIL_WHEN: Readonly<Partial<Record<NotificationEvent, string>>> = {
  // Approvals, the Sector Lead's OK of a delegate's entry, and over-budget decisions.
  delegate_approval_needed: 'a delegate’s entry needs the Sector Lead’s approval',
  overage_approval_needed: 'an award needs more than its pool has left',
  paa_taa_approval_needed: 'a PAA or TAA needs an Executive’s approval',
  self_recognition_approval_needed: 'an award needs an Executive’s approval',
  spot_committee_action_needed: 'a Spot nomination is ready to review',
  spot_supervisor_action_needed: 'a Spot recommendation needs a supervisor’s decision',
  spot_funding_action_needed: 'a Spot Award needs its funding decided',
  // An employee's election, and its deadline passing.
  election_opened: 'their award and election are ready',
  election_reminder: 'their election still needs a choice',
  election_past_deadline: 'an election closes with no choice',
  // The Stock Admin queue, and a request sent back to correct.
  stock_admin_queue: 'an award reaches the Stock Admin queue',
  check_returned: 'the Stock Admin team sends an award back to correct',
  // The Payroll and Shareworks handoffs.
  payroll_ready: 'a cash line is ready for Payroll',
  shareworks_ready: 'options or shares are ready for Shareworks',
};

/** The emails that ask someone to act: turning one off is confirmed with what stops. */
export const ACTION_EMAILS: ReadonlySet<NotificationEvent> = new Set(
  Object.keys(ACTION_EMAIL_WHEN).filter(isNotificationEvent),
);

/** The assistant's replies have one Copy switch together. */
export const ASSISTANT_COPY_KEY = 'assistant';
/** Help's message to the Stock Admin team, which a person asks for. */
export const HELP_HANDOFF_COPY_KEY = 'help_handoff';
/**
 * Feedback is seen only by System Admins and Support, so it's never copied (SENT-066), and its
 * email always goes as far as Settings › Messages is concerned.
 */
const FEEDBACK: ReadonlySet<string> = new Set(['feedback_replied', 'feedback_status_changed']);
/**
 * APP-264: nor is the Spot Awards pool's over-budget notice: a Spot Committee member on the copy
 * list would read about the pool (Tom's change 13). Settings › Messages says "Never copied".
 */
const NEVER_COPIED: ReadonlySet<string> = new Set([...FEEDBACK, 'spot_pool_over_budget']);

/**
 * APP-225: a notice nothing sends any more (the nominator's Spot outcome). Its definition stays for
 * what was sent before; Settings › Messages doesn't list it, so it has no Send switch either.
 */
const RETIRED: ReadonlySet<string> = new Set(['spot_nomination_decided']);

/**
 * How the organization's email switch for a notice is locked, as System administration ›
 * Notifications shows it: never emailed, always sent (a delegate's entry to the Sector Lead), or
 * only in the daily digest. Null: a switch.
 */
export function emailSwitchLock(
  event: NotificationEvent,
): 'not_offered' | 'always' | 'digest' | null {
  const d = notificationCatalog[event];
  return !d.emailSupported
    ? 'not_offered'
    : d.alwaysEmail
      ? 'always'
      : d.emailDelivery === 'digest'
        ? 'digest'
        : null;
}

/** A notice whose email is set somewhere else: the Go-live checklist, Elections & letters, the digest card. */
export function emailSetElsewhere(
  event: NotificationEvent,
): 'go_live' | 'manager_nudge' | 'digest' | null {
  return event === 'go_live_welcome'
    ? 'go_live'
    : event === 'manager_nudge'
      ? 'manager_nudge'
      : event === 'stock_admin_digest'
        ? 'digest'
        : null;
}

/**
 * Whether a notice has a Send switch in Settings › Messages: the one email the Stock Admin team
 * may turn on or off (SENT-075). The page and the server's check read this, so they can't drift.
 */
export function sendSwitchable(event: string): boolean {
  return (
    isNotificationEvent(event) &&
    emailSwitchLock(event) === null &&
    emailSetElsewhere(event) === null &&
    !FEEDBACK.has(event) &&
    !RETIRED.has(event)
  );
}

/**
 * An email already written, once its notice's email is turned off: someone turned it off (a saved
 * `email: false`), and it isn't one that always goes. Its mail row is then recorded as turned off
 * in Settings instead of sent (APP-194).
 */
export function sendTurnedOff(event: string, organization: unknown): boolean {
  if (!isNotificationEvent(event) || notificationCatalog[event].alwaysEmail) return false;
  if (typeof organization !== 'object' || organization === null) return false;
  const saved = Object.hasOwn(organization, event)
    ? (organization as Record<string, unknown>)[event]
    : undefined;
  return (
    typeof saved === 'object' &&
    saved !== null &&
    (saved as Record<string, unknown>)['email'] === false
  );
}

/** Every Copy switch: each automatic email, the Help hand-off, and the assistant's replies. */
export const COPY_SWITCH_KEYS: readonly string[] = [
  ...Object.entries(notificationCatalog)
    .filter(([event, d]) => d.emailSupported && !NEVER_COPIED.has(event))
    .map(([event]) => event),
  HELP_HANDOFF_COPY_KEY,
  ASSISTANT_COPY_KEY,
];

/**
 * The Copy switch an email reads, by the event its mail row is written with; null for an email
 * the copy list is never on (feedback, Send me a test, anything unknown).
 */
export function copySwitchKey(event: string): string | null {
  if (event === 'ai_assistant') return ASSISTANT_COPY_KEY;
  if (event === HELP_HANDOFF_COPY_KEY) return HELP_HANDOFF_COPY_KEY;
  if (!isNotificationEvent(event) || NEVER_COPIED.has(event)) return null;
  return notificationCatalog[event].emailSupported ? event : null;
}

/** On for automatic emails and the Help hand-off; off for the assistant's replies. */
export function copySwitchDefault(key: string): boolean {
  return key !== ASSISTANT_COPY_KEY;
}

/** Whether a Copy switch is on under the saved switches: its saved value, else its default. */
export function copySwitchOn(key: string, switches: unknown): boolean {
  const saved =
    typeof switches === 'object' && switches !== null && Object.hasOwn(switches, key)
      ? (switches as Record<string, unknown>)[key]
      : undefined;
  return typeof saved === 'boolean' ? saved : copySwitchDefault(key);
}

/** Whether the copy list goes on an email with this event, under the saved switches. */
export function copiesWanted(event: string, switches: unknown): boolean {
  const key = copySwitchKey(event);
  return key !== null && copySwitchOn(key, switches);
}

/** Said for a saved value that isn't a record of notices. */
const WHOLE_VALUE = '(the whole value)';

/** A notice's two switches as saved, or by default; null for a value that isn't them. */
function savedChannels(
  value: unknown,
  event: NotificationEvent,
): { email: boolean; inApp: boolean } | null {
  const d = notificationCatalog[event];
  if (value === undefined) return { email: d.defaultEmail, inApp: d.defaultInApp };
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  return Object.keys(v).length === 2 &&
    typeof v['email'] === 'boolean' &&
    typeof v['inApp'] === 'boolean'
    ? { email: v['email'], inApp: v['inApp'] }
    : null;
}

/**
 * What a change to `notifications.events` changes, notice by notice (APP-194): `email`, the
 * notices whose email alone changed; `other`, those where anything else did (an in-app switch, a
 * notice MyRewards doesn't have, a value it can't read). Each notice is compared by its switches
 * as saved, or by default, so adding or dropping a saved notice counts only for what it changes.
 * The Stock Admin team may make a change with nothing in `other`.
 */
export function eventSwitchChange(
  before: unknown,
  after: unknown,
): { readonly email: readonly string[]; readonly other: readonly string[] } {
  const asRecord = (v: unknown): Record<string, unknown> | null =>
    v === undefined || v === null
      ? {}
      : typeof v === 'object' && !Array.isArray(v)
        ? (v as Record<string, unknown>)
        : null;
  const was = asRecord(before);
  const now = asRecord(after);
  if (was === null || now === null) return { email: [], other: [WHOLE_VALUE] };
  const email: string[] = [];
  const other: string[] = [];
  for (const key of [...new Set([...Object.keys(was), ...Object.keys(now)])].sort()) {
    if (!isNotificationEvent(key)) {
      if (JSON.stringify(was[key]) !== JSON.stringify(now[key])) other.push(key);
      continue;
    }
    const a = savedChannels(was[key], key);
    const b = savedChannels(now[key], key);
    if (a === null || b === null || a.inApp !== b.inApp) other.push(key);
    // A locked notice's email isn't the Stock Admin team's to turn (SENT-075).
    else if (a.email !== b.email) (sendSwitchable(key) ? email : other).push(key);
  }
  return { email, other };
}
